// CountyMultiSelect.tsx — compact searchable multi-select for counties.
//
// Replaces the flat 100-checkbox `.alert-county-checklist` the alert settings
// used to render (one checkbox per NC county, which made the Settings panel
// several screens tall). Presentation only: the component owns no selection
// state. It renders `selected` as given and reports every check/uncheck
// through `onToggle(countyId, checked)` — the exact contract the old
// checkboxes had with AlertSettings' commitCounties — so plan caps, the
// server-side 422 `alert_county_limit` revert in useAlertPreferences, and the
// capError banner all behave exactly as before. If the save is reverted, the
// parent's `selected` changes and the chip/checkbox follow it.
//
// Search + option styling reuses CountyPickerModal's `.county-picker-search`
// and `.county-picker-option` rather than inventing a second look.

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { County } from "../types";

interface CountyMultiSelectProps {
  counties: County[];
  /** County ids currently selected (owned by the parent). */
  selected: string[];
  /** Same contract as the old checkbox onChange: one county, checked or not. */
  onToggle: (countyId: string, checked: boolean) => void;
  /** Accessible name for the control, e.g. "New Listings in Area counties". */
  label: string;
}

const countyLabel = (c: County) => `${c.name}, ${c.state}`;
const byName = (a: County, b: County) => a.name.localeCompare(b.name);

const CountyMultiSelect: React.FC<CountyMultiSelectProps> = ({
  counties,
  selected,
  onToggle,
  label,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Which ids are pinned to the top. Snapshotted when the popover opens, not
  // recomputed on every toggle: re-sorting live would move the option the
  // user just clicked out from under the pointer.
  const [pinned, setPinned] = useState<string[]>([]);

  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const popoverId = useId();

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Chips: selected counties we know about, alphabetical. Ids for counties
  // not in the list are left untouched in the saved prefs (same as the old
  // checklist, which simply had no checkbox for them).
  const chips = useMemo(
    () => counties.filter((c) => selectedSet.has(c.id)).sort(byName),
    [counties, selectedSet]
  );

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = counties
      .filter((c) => !q || countyLabel(c).toLowerCase().includes(q))
      .sort(byName);
    const pinnedSet = new Set(pinned);
    return [
      ...matches.filter((c) => pinnedSet.has(c.id)),
      ...matches.filter((c) => !pinnedSet.has(c.id)),
    ];
  }, [counties, query, pinned]);

  const openPopover = () => {
    if (open) return;
    setPinned(selected);
    setOpen(true);
  };

  // Plain function of setters + a ref, so the effect below can call it
  // without listing it as a dependency.
  const closePopover = useRef((returnFocus: boolean) => {
    setOpen(false);
    setQuery("");
    if (returnFocus) toggleRef.current?.focus();
  }).current;

  // Focus the search box when the popover opens.
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Escape + click-outside. Escape uses a NATIVE listener on the root: the
  // Settings dialog's focus trap listens natively on its container and
  // closes the whole panel on Escape, and a React onKeyDown would only run
  // after that (React listens at the app root). Stopping propagation here
  // closes just the popover.
  useEffect(() => {
    if (!open) return;
    const root = rootRef.current;
    if (!root) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      closePopover(true);
    };
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!root.contains(e.target as Node)) closePopover(false);
    };
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (next && !root.contains(next)) closePopover(false);
    };

    root.addEventListener("keydown", onKeyDown);
    root.addEventListener("focusout", onFocusOut);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      root.removeEventListener("keydown", onKeyDown);
      root.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open, closePopover]);

  return (
    <div className="county-multiselect" ref={rootRef}>
      <div
        className={"county-multiselect-control" + (open ? " county-multiselect-control--open" : "")}
        onClick={openPopover}
      >
        {chips.length === 0 ? (
          <span className="county-multiselect-placeholder">Choose counties</span>
        ) : (
          <ul className="county-multiselect-chips" aria-label={`Selected: ${label}`}>
            {chips.map((c) => (
              <li key={c.id} className="county-multiselect-chip">
                <span>{countyLabel(c)}</span>
                <button
                  type="button"
                  className="county-multiselect-chip-remove"
                  aria-label={`Remove ${countyLabel(c)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(c.id, false);
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          ref={toggleRef}
          className="county-multiselect-toggle"
          aria-label={open ? `Close ${label} list` : `Choose ${label}`}
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (open) closePopover(false);
            else openPopover();
          }}
        >
          <span aria-hidden="true">{open ? "▴" : "▾"}</span>
        </button>
      </div>

      {open && (
        <div className="county-multiselect-popover" id={popoverId}>
          <input
            ref={searchRef}
            type="search"
            className="county-picker-search county-multiselect-search"
            placeholder="Search counties…"
            aria-label="Search counties"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="county-multiselect-list" role="group" aria-label={label}>
            {options.length === 0 && (
              <p className="county-picker-loading">No counties match &ldquo;{query.trim()}&rdquo;.</p>
            )}
            {options.map((c) => (
              <label key={c.id} className="county-picker-option">
                <input
                  type="checkbox"
                  checked={selectedSet.has(c.id)}
                  onChange={(e) => onToggle(c.id, e.target.checked)}
                />
                <span>{countyLabel(c)}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountyMultiSelect;
